import { path, hasPath } from 'ramda'
import React from 'react'
import styled, { withTheme } from 'styled-components'

import { ColoredIcon } from '../../../../../src/ui/icons/Icon.jsx'
import { FlightLabel } from '../../../../../src/ui/labels/FlightLabel.jsx'
import { rgba } from '../../../../../src/utils/dom.js'
import { handleKeyboardSelect } from '../../../../utils/keyboardUtils.js'

const Wrapper = styled.button`
  padding: 12px 16px;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  border: none;
  background: white;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => rgba(theme.colors.primary, 0.05)};
  }
`

const AirportName = styled.div`
  font-weight: 500;
  ${({ theme }) => theme.fontSize('16px')};
  line-height: 24px;
  color: ${({ theme }) => theme.colors.primaryText};
`

const FlightName = styled.div`
  ${({ theme }) => theme.fontSize('14px')};
  line-height: 18px;
  color: ${({ theme }) => theme.colors.secondaryText};
  margin-bottom: 6px;
`

const FlightLabelStyled = styled(FlightLabel)`
  ${({ theme }) => theme.fontSize('12px')};
  line-height: 17px;
  margin-bottom: 6px;
`

const SpacedRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  & > div {
    display: flex;
  }
`

const InfoTitle = styled.div`
  font-weight: 500;
  ${({ theme }) => theme.fontSize('12px')};
  line-height: 17px;
  color: ${({ theme }) => theme.colors.secondaryText};
  text-transform: uppercase;
  margin-bottom: 1px;
`

const PrimaryInfo = styled.div`
  ${({ theme }) => theme.fontSize('20px')};
  line-height: 26px;
  color: ${({ theme }) => theme.colors.primaryText};
  margin-bottom: 4px;
  font-weight: 500;
`

const SecondaryInfo = styled(PrimaryInfo)`
  color: ${({ theme }) => rgba(theme.colors.primaryText, 0.7)};
  text-decoration-line: line-through;
  margin-right: 6px;
`

const AdditionalInfo = styled.div`
  ${({ theme }) => theme.fontSize('12px')};
  line-height: 18px;
  display: flex;
  align-items: center;
`

const AdditionalInfoText = styled.div`
  ${({ theme }) => theme.fontSize('14px')};
  line-height: 24px;
  color: ${({ theme }) => theme.colors.primaryText};
  margin-left: 4px;
`
// Empty airport name. Ex: To ()
const regx = /\(\)/

export const FlightListElement = withTheme(({
  className,
  onFlightClick,
  flight,
  theme,
  T
}) => <Wrapper
  data-cy='FlightListElement'
  className={className}
  onClick={() => onFlightClick(flight.flightId)}
  aria-label={`${T('flightDetails:Flight Name')}: ${flight.name} ${regx.test(flight.connectionAirport) ? T('flightDetails:Flight Info Unavailable') : flight.connectionAirport} ${T('flightDetails:Flight Status')}: ${flight.statusText}, ${T('flightDetails:Gate')}: ${flight.gate.name} ${T('flightDetails:Date')}: ${flight.date} ${T('flightDetails:Original Time')}: ${flight.oldTime} ${T('flightDetails:Time')}: ${flight.time} ${path(['gate', 'position', 'structureName'], flight)} `}
  onKeyDown={handleKeyboardSelect(() => onFlightClick(flight.flightId))}>
  <AirportName>{regx.test(flight.connectionAirport) ? T('flightDetails:Flight Info Unavailable') : flight.connectionAirport}</AirportName>
  <FlightName>{flight.name}</FlightName>
  <FlightLabelStyled status={flight.status} statusText={flight.statusText} />

  <SpacedRow>
    <InfoTitle>{flight.date}</InfoTitle>
    <InfoTitle>{flight.gateLabel}</InfoTitle>
  </SpacedRow>

  <SpacedRow>
    <div>
      {!!flight.oldTime && <SecondaryInfo>{flight.oldTime}</SecondaryInfo>}
      <PrimaryInfo>{flight.time}</PrimaryInfo>
    </div>
    <PrimaryInfo>{flight.gate.name}</PrimaryInfo>
  </SpacedRow>

  <AdditionalInfo flight={flight} />

  {hasPath(['gate', 'position', 'structureName'], flight) && <AdditionalInfo>
    <ColoredIcon id='building' width={24} height={24} fillColor={theme.colors.primaryText} />
    <AdditionalInfoText>{path(['gate', 'position', 'structureName'], flight)}</AdditionalInfoText>
  </AdditionalInfo>}

  {!!flight.baggageClaim && <AdditionalInfo>
    <ColoredIcon id='bag-claim' width={24} height={24} fillColor={theme.colors.primaryText} />
    <AdditionalInfoText>{flight.baggageClaim}</AdditionalInfoText>
  </AdditionalInfo>}

</Wrapper>
)
